import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, LoadingSkeleton, Tabs } from '@nexora/design-system';
import { formatCurrency, formatDate, formatDocument } from '../../shared/utils/format';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { useAuth } from '../../shared/auth/AuthContext';
import { useCustomer, useCustomers } from '../master-data/useMasterData';
import { useQuotes } from '../sales/useQuotes';
import { useInteractions, useCreateInteraction, type InteractionType } from './useInteractions';
import { ContratosDoCliente } from './CustomerContracts';

type FichaTab = '360' | 'contratos' | 'interacoes' | 'pedidos';

interface SalesOrder {
  id: string;
  customerId: string;
  customerName: string | null;
  status: string;
  grandTotal: number;
  createdAt: string;
}

interface PaginatedOrders {
  data: SalesOrder[];
  total: number;
}

const TABS: Array<{ key: FichaTab; label: string }> = [
  { key: '360', label: 'Visão 360' },
  { key: 'contratos', label: 'Contratos' },
  { key: 'interacoes', label: 'Interações' },
  { key: 'pedidos', label: 'Pedidos e Vendas' },
];

export function CustomerFichaPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { customerId?: string };
  const customerId = params.customerId;
  const [tab, setTab] = useState<FichaTab>('360');

  if (!customerId) {
    return <FichaPicker />;
  }

  return (
    <div className="p-xl">
      <FichaHeader customerId={customerId} onBack={() => void navigate({ to: '/crm/customers' })} />
      <div className="mb-lg">
        <Tabs
          tabs={TABS}
          value={tab}
          onChange={(key) => setTab(key as FichaTab)}
          aria-label="Abas da ficha do cliente"
        />
      </div>
      {tab === '360' ? <Visao360 customerId={customerId} /> : null}
      {tab === 'contratos' ? <ContratosDoCliente customerId={customerId} /> : null}
      {tab === 'interacoes' ? <InteracoesDoCliente customerId={customerId} /> : null}
      {tab === 'pedidos' ? <PedidosDoCliente customerId={customerId} /> : null}
    </div>
  );
}

function FichaPicker(): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, error } = useCustomers();
  const customers = data ?? [];

  return (
    <div className="p-xl">
      <div className="mb-lg">
        <h1 className="m-0 font-display-lg text-display-lg text-on-surface">Ficha do Cliente</h1>
        <p className="mt-xs font-body-md text-body-md text-on-surface-variant">
          Selecione um cliente para abrir a visão 360.
        </p>
      </div>
      {isLoading ? (
        <LoadingSkeleton count={6} height="40px" />
      ) : error ? (
        <p className="font-body-md text-body-md text-error">{(error as Error).message}</p>
      ) : customers.length === 0 ? (
        <Card>
          <p className="mb-md font-body-md text-body-md text-on-surface-variant">
            Nenhum cliente cadastrado.
          </p>
          <Button variant="primary" onClick={() => void navigate({ to: '/master-data/customers/new' })}>
            Novo cliente
          </Button>
        </Card>
      ) : (
        <Card padding="0">
          <ul className="m-0 list-none p-0">
            {customers.map((c) => (
              <li key={c.id} className="border-b border-outline-variant last:border-b-0">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-md py-sm text-left hover:bg-surface-container"
                  onClick={() =>
                    void navigate({ to: `/crm/customers/${c.id}` })
                  }
                >
                  <span>
                    <span className="block font-body-md text-body-md font-semibold text-on-surface">
                      {c.name}
                    </span>
                    <span className="font-body-sm text-body-sm text-on-surface-variant">
                      {formatDocument(c.documentNumber, c.documentType as 'CPF' | 'CNPJ' | undefined)}
                    </span>
                  </span>
                  <Badge variant={c.isBlocked ? 'error' : 'success'}>
                    {c.isBlocked ? 'Bloqueado' : 'Ativo'}
                  </Badge>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function FichaHeader({ customerId, onBack }: { customerId: string; onBack: () => void }): JSX.Element {
  const { data, isLoading, error } = useCustomer(customerId);

  if (isLoading) return <LoadingSkeleton count={2} height="32px" />;
  if (error || !data) {
    return (
      <div className="mb-lg">
        <p className="font-body-md text-body-md text-error">
          {(error as Error)?.message ?? 'Cliente não encontrado'}
        </p>
        <Button variant="outline" onClick={onBack}>
          Voltar à lista
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-lg flex flex-col gap-md sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="m-0 font-display-lg text-display-lg text-on-surface">{data.name}</h1>
        <p className="mt-xs font-body-md text-body-md text-on-surface-variant">
          {formatDocument(data.documentNumber, data.documentType as 'CPF' | 'CNPJ' | undefined)}
          {data.email ? ` · ${data.email}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-sm">
        <Badge variant={data.isBlocked ? 'error' : 'success'}>
          {data.isBlocked ? 'Bloqueado' : 'Ativo'}
        </Badge>
        <Button variant="outline" onClick={onBack}>
          Trocar cliente
        </Button>
      </div>
    </div>
  );
}

function Visao360({ customerId }: { customerId: string }): JSX.Element {
  const { data, isLoading, error } = useCustomer(customerId);
  const quotesQuery = useQuotes({ customerId, page: 1, limit: 50 });

  if (isLoading) return <LoadingSkeleton count={4} height="64px" />;
  if (error || !data) {
    return <p className="font-body-md text-body-md text-error">{(error as Error)?.message}</p>;
  }

  const quotes = quotesQuery.data?.data ?? [];
  const abertas = quotes.filter((q) => q.status === 'DRAFT' || q.status === 'SENT');
  const addr = data.address;

  return (
    <div className="grid grid-cols-1 gap-md lg:grid-cols-3">
      <Card>
        <h3 className="mt-0 font-headline-sm text-headline-sm">Cadastro</h3>
        <Dl label="Documento" value={formatDocument(data.documentNumber, data.documentType as 'CPF' | 'CNPJ' | undefined)} />
        <Dl label="E-mail" value={data.email ?? '—'} />
        <Dl label="Telefone" value={data.phone ?? data.mobile ?? '—'} />
        <Dl label="Cliente desde" value={formatDate(data.createdAt)} />
      </Card>
      <Card>
        <h3 className="mt-0 font-headline-sm text-headline-sm">Comercial</h3>
        <Dl label="Limite de crédito" value={formatCurrency(data.creditLimit ?? 0)} />
        <Dl label="Condição de pagamento" value={data.paymentTerms ?? '—'} />
        <Dl label="Tabela de preço" value={data.priceTable ?? '—'} />
        <Dl label="Oportunidades abertas" value={String(abertas.length)} />
      </Card>
      <Card>
        <h3 className="mt-0 font-headline-sm text-headline-sm">Endereço</h3>
        <p className="font-body-md text-body-md text-on-surface">
          {addr
            ? [addr.street, addr.number, addr.district, addr.city, addr.state, addr.zipCode]
                .filter(Boolean)
                .join(', ') || '—'
            : '—'}
        </p>
        {data.notes ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant">{data.notes}</p>
        ) : null}
      </Card>
    </div>
  );
}

function PedidosDoCliente({ customerId }: { customerId: string }): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const { data, isLoading, error } = useQuery({
    queryKey: [...queryKeys.sales.list(tenantId), 'ficha', customerId],
    queryFn: () =>
      apiClient.get<PaginatedOrders>('/orders', {
        params: { customerId, page: '1', limit: '50' },
      }),
    enabled: !!tenantId,
  });

  const rows = data?.data ?? [];
  const total = rows.reduce((sum, o) => sum + o.grandTotal, 0);

  if (isLoading) return <LoadingSkeleton count={5} height="36px" />;
  if (error) {
    return <p className="font-body-md text-body-md text-error">{(error as Error).message}</p>;
  }
  if (rows.length === 0) {
    return <EmptyPanel title="Nenhum pedido" body="Este cliente ainda não tem pedidos de venda." />;
  }

  return (
    <Card padding="0">
      <div className="flex items-center justify-between border-b border-outline-variant p-md">
        <h3 className="m-0 font-headline-sm text-headline-sm">Pedidos</h3>
        <span className="font-label-md text-label-md text-on-surface-variant">
          {rows.length} · {formatCurrency(total)}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[0.875rem]">
          <thead>
            <tr>
              <th className="border-b-2 border-outline-variant bg-surface-container-low px-md py-sm text-left font-label-sm text-label-sm uppercase text-on-surface-variant">
                Identificador
              </th>
              <th className="border-b-2 border-outline-variant bg-surface-container-low px-md py-sm text-left font-label-sm text-label-sm uppercase text-on-surface-variant">
                Status
              </th>
              <th className="border-b-2 border-outline-variant bg-surface-container-low px-md py-sm text-left font-label-sm text-label-sm uppercase text-on-surface-variant">
                Valor
              </th>
              <th className="border-b-2 border-outline-variant bg-surface-container-low px-md py-sm text-left font-label-sm text-label-sm uppercase text-on-surface-variant">
                Criado em
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((order) => (
              <tr key={order.id}>
                <td className="border-b border-outline-variant px-md py-sm font-mono text-on-surface">
                  {order.id.slice(0, 8)}
                </td>
                <td className="border-b border-outline-variant px-md py-sm">{order.status}</td>
                <td className="border-b border-outline-variant px-md py-sm">
                  {formatCurrency(order.grandTotal)}
                </td>
                <td className="border-b border-outline-variant px-md py-sm">{formatDate(order.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function EmptyPanel({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <Card>
      <p className="mb-xs mt-0 font-headline-sm text-headline-sm text-on-surface">{title}</p>
      <p className="m-0 font-body-md text-body-md text-on-surface-variant">{body}</p>
    </Card>
  );
}

const INTERACTION_TYPES: Array<{ id: InteractionType; label: string }> = [
  { id: 'CALL', label: 'Ligação' },
  { id: 'EMAIL', label: 'E-mail' },
  { id: 'MEETING', label: 'Reunião' },
  { id: 'NOTE', label: 'Nota' },
  { id: 'WHATSAPP', label: 'WhatsApp' },
];

function InteracoesDoCliente({ customerId }: { customerId: string }): JSX.Element {
  const { data, isLoading, error } = useInteractions({ customerId, limit: 50 });
  const createInteraction = useCreateInteraction();
  const [type, setType] = useState<InteractionType>('NOTE');
  const [notes, setNotes] = useState('');
  const [participants, setParticipants] = useState('');
  const [formError, setFormError] = useState('');

  const rows = data?.data ?? [];

  const submit = (): void => {
    if (!notes.trim()) {
      setFormError('Descreva a interação.');
      return;
    }
    setFormError('');
    createInteraction.mutate(
      {
        customerId,
        type,
        notes: notes.trim(),
        ...(participants.trim() ? { participants: participants.trim() } : {}),
      },
      {
        onSuccess: () => {
          setNotes('');
          setParticipants('');
        },
        onError: (err) => setFormError((err as Error).message),
      },
    );
  };

  return (
    <div className="flex flex-col gap-md">
      <Card>
        <h3 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">
          Registrar interação
        </h3>
        <div className="mb-sm flex flex-wrap gap-sm">
          {INTERACTION_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setType(t.id)}
              aria-pressed={type === t.id}
              className={`rounded-md border px-sm py-xs font-label-md text-label-md transition-colors ${
                type === t.id
                  ? 'border-primary bg-primary-container font-semibold text-on-primary-container'
                  : 'border-outline-variant text-on-surface-variant hover:bg-surface-container-low'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <textarea
          aria-label="Notas da interação"
          className="mb-sm w-full rounded-md border border-outline-variant bg-surface-container-lowest px-sm py-xs font-body-md text-body-md text-on-surface"
          rows={3}
          placeholder="O que foi tratado…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <input
          aria-label="Participantes"
          className="mb-sm w-full rounded-md border border-outline-variant bg-surface-container-lowest px-sm py-xs font-body-md text-body-md text-on-surface"
          placeholder="Participantes (texto livre, opcional)"
          value={participants}
          onChange={(e) => setParticipants(e.target.value)}
        />
        {formError ? (
          <p className="m-0 mb-sm font-body-sm text-body-sm text-error">{formError}</p>
        ) : null}
        <Button
          variant="primary"
          loading={createInteraction.isPending}
          onClick={submit}
        >
          Salvar interação
        </Button>
      </Card>

      {isLoading ? (
        <LoadingSkeleton count={3} height="64px" />
      ) : error ? (
        <p className="font-body-md text-body-md text-error">{(error as Error).message}</p>
      ) : rows.length === 0 ? (
        <EmptyPanel
          title="Nenhuma interação"
          body="Este cliente ainda não tem histórico registrado."
        />
      ) : (
        <Card padding="0">
          <div className="flex items-center justify-between border-b border-outline-variant p-md">
            <h3 className="m-0 font-headline-sm text-headline-sm">Histórico</h3>
            <span className="font-label-md text-label-md text-on-surface-variant">
              {rows.length} registro{rows.length === 1 ? '' : 's'}
            </span>
          </div>
          <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
            {rows.map((interaction) => (
              <li
                key={interaction.id}
                className="border-b border-outline-variant p-md last:border-b-0"
              >
                <div className="mb-xs flex flex-wrap items-center gap-sm">
                  <span className="rounded-sm bg-primary-container px-xs py-[2px] font-label-sm text-label-sm font-semibold text-on-primary-container">
                    {INTERACTION_TYPES.find((t) => t.id === interaction.type)?.label ??
                      interaction.type}
                  </span>
                  <span className="font-label-sm text-label-sm text-on-surface-variant">
                    {formatDate(interaction.occurredAt)}
                  </span>
                </div>
                <p className="m-0 mb-xs font-body-md text-body-md text-on-surface">
                  {interaction.notes}
                </p>
                {interaction.participants ? (
                  <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
                    Com: {interaction.participants}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Dl({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="mb-sm">
      <div className="font-label-sm text-label-sm uppercase text-on-surface-variant">{label}</div>
      <div className="font-body-md text-body-md text-on-surface">{value}</div>
    </div>
  );
}
